import { OtpError } from './otp.types';
import { getApiUrl } from '../helper/getApiUrl';

import type {
    OtpIntentPayload,
    OtpIntentResult,
    OtpVerificationCapabilitiesResult,
    OtpVerifyPayload,
    OtpVerifyResult,
} from './otp.types';

const apiUrl = getApiUrl();

const getLocale = (): 'en' | 'de' => {
    const lang = window.navigator.language.split('-')[0].toLowerCase();
    return lang === 'de' ? 'de' : 'en';
};

const createOtpIntent = async (payload: OtpIntentPayload): Promise<string> => {
    const response = await fetch(`${apiUrl}/v2/verification-intent`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...payload, locale: getLocale() }),
    });

    const json = (await response.json()) as OtpIntentResult;

    if (json.success) {
        return json.data.verificationIntent.id;
    }

    throw new Error(json.errors?.[0]?.message ?? 'Unknown error');
};

const verifyOtp = async (payload: OtpVerifyPayload): Promise<boolean> => {
    const response = await fetch(`${apiUrl}/verify-otp/${payload.verificationIntentId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            otp: payload.otp,
        }),
    });

    const json = (await response.json()) as OtpVerifyResult;

    if (json.success) {
        return true;
    }

    const errorMessage = json.errors?.[0]?.message ?? 'Unknown error';

    switch (response.status) {
        case 400:
            throw new OtpError(400, 'INVALID_OTP', errorMessage);
        case 404:
            throw new OtpError(404, 'NOT_FOUND', errorMessage);
        case 410:
            throw new OtpError(410, 'EXPIRED', errorMessage);
        case 429:
            throw new OtpError(429, 'TOO_MANY_ATTEMPTS', errorMessage);
        default:
            throw new Error(errorMessage);
    }
};

const getOtpCapabilities = async (
    companyId: string,
): Promise<OtpVerificationCapabilitiesResult> => {
    const response = await fetch(`${apiUrl}/verification-capabilities/${companyId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const json = (await response.json()) as OtpVerificationCapabilitiesResult;

    return json;
};

export const otpApi = {
    createOtpIntent,
    verifyOtp,
    getOtpCapabilities,
};
